(() => {
  if (window.EmbryniumProductState) return;

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/grey/g, 'gray')
    .replace(/colour/g, 'color')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const archiveToken = (product) => {
    const match = `${product?.handle || ''} ${product?.title || ''}`.match(/archive[-\s]?(\d{3})/i);
    return match ? `archive ${match[1]}` : '';
  };

  const containsPhrase = (text, expected) => {
    if (!expected) return true;
    const haystack = ` ${normalize(text)} `;
    const needle = ` ${normalize(expected)} `;
    return haystack.includes(needle);
  };

  const belongsToProduct = (media, product) => {
    const expected = archiveToken(product);
    if (!expected) return true;
    const found = normalize(media?.alt).match(/archive\s+(\d{3})/);
    return !found || `archive ${found[1]}` === expected;
  };

  const create = (config) => {
    const product = config || {};
    const variants = (product.variants || []).map((variant) => ({
      ...variant,
      id: String(variant.id),
      options: (variant.options || []).map(String),
      price: Number(variant.price || 0),
      compareAtPrice: Number(variant.compareAtPrice || 0),
    }));
    const media = (product.media || []).filter((item) => item?.type === 'image');
    const optionNames = (product.options || []).map((option) => normalize(option.name));
    const colorIndex = optionNames.findIndex((name) => name.includes('color'));

    const resolveVariant = (selection = []) => variants.find((variant) =>
      variant.options.every((value, index) => normalize(value) === normalize(selection[index]))
    ) || null;

    const scoreVariant = (variant, selection, preferredIndex = -1) => {
      let score = 0;
      variant.options.forEach((value, index) => {
        if (normalize(value) === normalize(selection[index])) score += index === preferredIndex ? 20 : 4;
      });
      return score;
    };

    const closestVariant = (selection = [], { availableOnly = true, preferredIndex = -1 } = {}) => {
      const pool = variants.filter((variant) => !availableOnly || variant.available);
      if (!pool.length) return null;
      return [...pool].sort((a, b) => scoreVariant(b, selection, preferredIndex) - scoreVariant(a, selection, preferredIndex))[0] || null;
    };

    const coerceSelection = (selection = [], changedIndex = -1) => {
      const exact = resolveVariant(selection);
      if (exact?.available) return { selection: [...exact.options], variant: exact };

      // Preserve the option the shopper intentionally changed, then resolve the
      // remaining options to the closest available Shopify variant.
      const requestedValue = changedIndex >= 0 ? normalize(selection[changedIndex]) : '';
      const preferredPool = variants.filter((variant) => variant.available && (
        changedIndex < 0 || normalize(variant.options[changedIndex]) === requestedValue
      ));
      if (preferredPool.length) {
        const closest = [...preferredPool].sort((a, b) => scoreVariant(b, selection, changedIndex) - scoreVariant(a, selection, changedIndex))[0];
        return { selection: [...closest.options], variant: closest };
      }

      const closest = closestVariant(selection, { availableOnly: true, preferredIndex: changedIndex });
      if (closest) return { selection: [...closest.options], variant: closest };
      if (exact) return { selection: [...exact.options], variant: exact };
      const any = closestVariant(selection, { availableOnly: false, preferredIndex: changedIndex });
      return { selection: any ? [...any.options] : [...selection], variant: any };
    };

    // Whether an option value can be selected at all. This intentionally does
    // not lock color to the currently selected size; selecting a new color may
    // legitimately resolve the size to the closest available variant, exactly
    // like the catalog card.
    const valueAvailable = (optionIndex, value, selection = []) => variants.some((variant) => {
      if (!variant.available || normalize(variant.options[optionIndex]) !== normalize(value)) return false;
      if (optionIndex === colorIndex) return true;
      return variant.options.every((variantValue, index) => {
        if (index === optionIndex) return true;
        if (index === colorIndex && selection[index]) return normalize(variantValue) === normalize(selection[index]);
        return true;
      });
    });

    const eligibleMedia = () => media.filter((item) => belongsToProduct(item, product));

    const exactMedia = ({ selection = [], view = 'front' } = {}) => {
      const desiredView = normalize(view) === 'back' ? 'back' : 'front';
      const color = colorIndex >= 0 ? normalize(selection[colorIndex]) : '';
      const pool = eligibleMedia();

      // Shopify product media + merchant alt text are authoritative. A media
      // item only qualifies as an exact match when BOTH the selected color and
      // requested Front/Back label are present in that media's alt text.
      if (color) {
        const matches = pool.filter((item) => containsPhrase(item.alt, color) && containsPhrase(item.alt, desiredView));
        if (!matches.length) return null;
        // Prefer an image whose alt also names this Archive/product. Stable
        // media order remains the final tiebreaker.
        const token = archiveToken(product);
        return matches.find((item) => !token || containsPhrase(item.alt, token)) || matches[0];
      }
      return pool.find((item) => containsPhrase(item.alt, desiredView)) || null;
    };

    const resolveMedia = ({ selection = [], view = product.defaultView || 'front', variant = null } = {}) => {
      const desiredView = normalize(view) === 'back' ? 'back' : 'front';
      const color = colorIndex >= 0 ? normalize(selection[colorIndex]) : '';
      const pool = eligibleMedia();

      const exact = exactMedia({ selection, view: desiredView });
      if (exact) return exact;

      // Never show the back of a different color. If a selected color lacks a
      // labeled back image, return null so the Back control is disabled.
      if (desiredView === 'back' && color) return null;

      // For front view, a variant featured image is a safe fallback only when
      // it belongs to the selected color (or when the product has no color option).
      const featured = variant?.featuredMediaId
        ? pool.find((item) => String(item.id) === String(variant.featuredMediaId))
        : null;
      if (featured && (!color || containsPhrase(featured.alt, color))) return featured;

      if (color) {
        const sameColor = pool.find((item) => containsPhrase(item.alt, color));
        if (sameColor) return sameColor;
      }

      return pool.find((item) => containsPhrase(item.alt, desiredView)) || featured || pool[0] || null;
    };

    const hasView = ({ selection = [], view = 'front' } = {}) => {
      const desiredView = normalize(view) === 'back' ? 'back' : 'front';
      if (desiredView === 'back') return Boolean(exactMedia({ selection, view: 'back' }));
      return Boolean(resolveMedia({ selection, view: 'front' }));
    };

    return {
      product,
      variants,
      media,
      colorIndex,
      resolveVariant,
      closestVariant,
      coerceSelection,
      valueAvailable,
      resolveMedia,
      hasView,
    };
  };

  const read = (id) => {
    const node = document.getElementById(id);
    if (!node) return null;
    try { return create(JSON.parse(node.textContent)); }
    catch (error) {
      console.error('Embrynium product configuration could not be read.', error);
      return null;
    }
  };

  window.EmbryniumProductState = Object.freeze({ create, read, normalize });
})();
